import { defineComponent, ref, Ref, computed, watch} from 'vue';
import {
    HoursEntryModel, HoursExceptionModel, HoursOfOperationHolidaysCondition,
    HoursOfOperationType, Listing, UpdateHoursLocationModel, UpdateHolidaysLocationModel, UpdateHoursExceptionsLocationModel, IHoursOfOperationManager
} from '@truckdown/locations';
import context from '@truckdown/components-systems';
import weeklySchedule from './WeeklySchedule/WeeklySchedule.vue';
import hoursExceptions from './HoursExceptions/HoursExceptions.vue';
import holidayHours from './HolidayHours/HolidayHours.vue';
import hoursTableNavigation from './HoursTableNavigation/HoursTableNavigation.vue';
import hoursSettings from './HoursSettings/HoursSettings.vue';
import { prepareApiEntriesFromUi, getAfterHoursType, mergeMidnightEntriesFromApi, showMessage, removeEmptyTimeEntries} from '../../utilities';

enum HoursTab {
    Hours = 1,
    Exceptions = 2,
    HolidayHours = 3
}

interface IData {
    model: Ref<UpdateHoursLocationModel>,
    exceptionsModel: Ref<any>,
    holidaysModel: Ref<any>,
    allEntries: Ref<{ [key: string]: HoursEntryModel }>,
    showAddException: Ref<boolean>,
    tab: Ref<HoursTab>,
    isExceptionsValid: Ref<boolean>,
    isHoursValid: Ref<boolean>,
    isLoading: Ref<boolean>,
    originalHoursModel: Ref<UpdateHoursLocationModel>,
    originalExceptions: Ref<HoursExceptionModel[]>,
    originalHoildays: Ref<any>
}

const getInitialData = function (props: any): IData {
    let hoursModel = {
        id: props.listing.hoursData?.id || undefined,
        locationId: props.listing.id.toString(),
        createTask: !props.isAdmin,
        accountId: props.listing.accountId,
        type: props.listing.hoursData?.type ?? HoursOfOperationType.Unknown,
        majorHolidaysCondition: props.listing.hoursData?.majorHolidayCondition || HoursOfOperationHolidaysCondition.RegularHours,
        entries: props.listing.hoursData?.entries || {}
    } as UpdateHoursLocationModel;
    return {
        model: ref<UpdateHoursLocationModel>(hoursModel),
        exceptionsModel: ref<any>({
            accountId: props.listing.accountId,
            locationId: props.listing.id.toString(),
            createTask: !props.isAdmin,
            exceptions: props.listing.hoursData?.exceptions as HoursExceptionModel[] || []
        }),
        holidaysModel: ref<any>({
            accountId: props.listing.accountId,
            locationId: props.listing.id.toString(),
            holidays: props.listing.hoursData?.holidays || {}
        }),
        allEntries: ref<{ [key: string]: HoursEntryModel }>(mergeMidnightEntriesFromApi(props.listing.hoursData?.entries || {})),
        showAddException: ref<boolean>(false),
        tab: ref<HoursTab>(props.listing.hoursData?.type == HoursOfOperationType.SelectHours ? HoursTab.Hours : HoursTab.Exceptions),
        isExceptionsValid: ref<boolean>(true),
        isHoursValid: ref<boolean>(true),
        isLoading: ref<boolean>(false),
        originalHoursModel: ref<UpdateHoursLocationModel>(hoursModel),
        originalExceptions: ref<HoursExceptionModel[]>(props.listing.hoursData?.exceptions as HoursExceptionModel[] || []),
        originalHoildays: ref<any>(props.listing.hoursData?.holidays || {})
    };
}

const getComputed = function (props: any) {
    return {
        afterHoursType: computed(() => {
            return getAfterHoursType(props.listing);
        }),
        listingHolidayCondition: computed(() => {
            return props.listing.hoursData?.majorHolidayCondition || HoursOfOperationHolidaysCondition.RegularHours;
        }),
        listingHolidays: computed(() => {
            return props.listing.hoursData?.holidays || {};
        }),
        allExceptions: computed(() => {
            return props.listing.hoursData?.exceptions || [];
        }),
        listingTimezone: computed(() => {
            return props.listing.hoursData?.timezone || "";
        })
    };
}

const getMethods = function (data: IData, props: any, ctx: any) {
    async function updateHours(): Promise<boolean> {
        if (data.model.value) {
            const manager = await context.getService<IHoursOfOperationManager>('IHoursOfOperationManager');
            data.model.value.entries = removeEmptyTimeEntries(data.model.value.entries);
            const result = await manager.updateHoursLocation(data.model.value);

            if (result && result.ok) {
                showMessage("Listing hours were successfully updated", "success", true, true);
                ctx.emit("update-hours");
                return true;
            } else if (!result?.ok) {
                showMessage(result.message || "An error occured while updating the listing hours", "danger", true, true);
            }
            return false;
        } else {
            return false;
        }
    }

    async function updateHolidays(): Promise<boolean> {
        const manager = await context.getService<IHoursOfOperationManager>('IHoursOfOperationManager');
        const result = await manager.updateHolidaysLocation(data.holidaysModel.value);

        if (result && result.ok) {
            showMessage("Listing holiday hours were successfully updated", "success", true, true);
            ctx.emit("update-hours");
            return true;
        } else if (!result?.ok) {
            showMessage(result.message || "An error occured while updating the listing holiday hours", "danger", true, true);
        }
        return false;
    }

    async function updateExceptions(): Promise<boolean> {
        if (data.exceptionsModel.value) {
            const manager = await context.getService<IHoursOfOperationManager>('IHoursOfOperationManager');
            const result = await manager.updateHoursExceptionsLocation(data.exceptionsModel.value);

            if (result && result.ok) {
                showMessage("Listing hours exceptions were successfully updated", "success", true, true);
                ctx.emit("update-hours");
                return true;
            } else if (!result?.ok) {
                showMessage(result.message || "An error occured while updating the listing hours exceptions", "danger", true, true);
            }
            return false;
        } else {
            return false;
        }
    }

    return {
        updateNavigation: function (tab: HoursTab, buttonState: boolean) {
            data.tab.value = tab;
            data.showAddException.value = buttonState;
        },
        updateModel: function (model: any) {
            data.model.value.type = model.type;
            if (data.model.value.type == HoursOfOperationType.SelectHours) {
                data.tab.value = HoursTab.Hours;
            }
            if (data.model.value.majorHolidaysCondition != model.majorHolidaysCondition) {
                data.tab.value = HoursTab.HolidayHours;
            }
            data.model.value.majorHolidaysCondition = model.majorHolidaysCondition;
        },
        updateEntries: function (entries: { [key: string]: HoursEntryModel }, isValid: boolean) {
            data.model.value.entries = prepareApiEntriesFromUi(entries);
            data.isHoursValid.value = isValid;
        },
        updateExceptions: async function (exceptions: HoursExceptionModel[], isValid: boolean) {
            data.exceptionsModel.value.exceptions = exceptions;
            data.showAddException.value = false;
            data.isExceptionsValid.value = isValid;
            if (isValid) {
                await updateExceptions();
            }
        },
        updateHolidays: function (holidays: any[]) {
            data.holidaysModel.value.holidays = holidays;
        },
        cancelUpdate: function () {
            data.showAddException.value = false;
        },
        saveHours: async function (ev: Event) {
            if (data.isHoursValid.value && data.model.value && !data.isLoading.value) {
                data.isLoading.value = true;
                try {
                    const hoursOk = await updateHours();
                    data.isLoading.value = false;
                    if (!hoursOk) return;
                   
                } catch (ex) {
                    console.error(ex);
                    showMessage("There was an error saving hours", "danger", true, true);
                }
            }
        },
        saveExceptions: async function (ev: Event) {
            if (data.isExceptionsValid.value && data.exceptionsModel.value && !data.isLoading.value) {
                data.isLoading.value = true;
                try {
                    const exceptionsOk = await updateExceptions();
                    data.isLoading.value = false;
                    if (!exceptionsOk) return;
                   
                } catch (ex) {
                    console.error(ex);
                    showMessage("There was an error saving hours exceptions", "danger", true, true);
                }
            }
        },
        saveHolidayHours: async function (ev: Event) {
            if (data.holidaysModel.value && !data.isLoading.value) {
                data.isLoading.value = true;
                try {
                    const holidaysOk = await updateHolidays();
                    data.isLoading.value = false;
                    if (!holidaysOk) return;
                } catch (ex) {
                    console.error(ex);
                    showMessage("There was an error saving holiday hours", "danger", true, true);
                }
            }
        },
        clearHours: function () {
            data.model.value.entries = {};
            data.allEntries.value = {};
        }
    };
}

export default defineComponent({
    props: {
        listing: {
            type: Object as () => Listing,
            required: false 
        },
        regions: Array,
        isAdmin: {
            type: Boolean,
            default: false,
            required: false
        }
    },
    emits: ['submit-edit', 'update-hours'],
    components: {
        'weekly-schedule': weeklySchedule,
        'hours-exceptions': hoursExceptions,
        'holiday-hours': holidayHours,
        'hours-settings': hoursSettings,
        'hours-table-navigation': hoursTableNavigation
    },
    setup: function (props, ctx) {
        var data = getInitialData(props);
        var methods = getMethods(data, props, ctx);
        var computed = getComputed(props);


        watch(
            () => [props.listing?.hoursData],
            ([newVal, oldVal]) => {
                if (newVal != undefined) {
                    data.model.value.type = newVal?.type ?? HoursOfOperationType.Unknown;
                    data.model.value.majorHolidaysCondition = newVal?.majorHolidayCondition || HoursOfOperationHolidaysCondition.RegularHours;
                    data.model.value.entries = newVal?.entries || {};
                    data.allEntries.value = mergeMidnightEntriesFromApi(newVal?.entries || {});
                }
            },
            {
                deep: true
            }
           
        );

        return {
            ...data,
            ...methods,
            ...computed,
            'HoursOfOperationType': HoursOfOperationType,
            'HoursTab': HoursTab,
            'HoursOfOperationHolidaysCondition': HoursOfOperationHolidaysCondition
        }
    }
});

